import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { JobCatsOne } from '../vacancy-data.component'
import { matchesLocation, normalizePlace } from '../vacancy-filters'
import { displayCity } from '../vacancy-format'

export interface SearchSuggestion {
  value: string
  /** Vacancies this suggestion returns; absent where suggestions are plain completions. */
  count?: number
}

export interface SearchSuggestionGroup {
  label: string
  options: SearchSuggestion[]
}

/**
 * Powers the Functie + Locatie autocomplete on /vacatures.
 *
 * Profession suggestions are *cleaned* — raw CATS titles like
 * "Infrastructuur Engineer – Linux – Utrecht" are split on common role
 * separators and only the leading role phrase is indexed. Location and
 * keyword fallbacks are merged in, deduped case-insensitively, and ranked
 * by frequency. The same normalization + synonym expansion used here is
 * also exposed via `matchesTitle` so filterJobs() can stay in sync with
 * what the autocomplete promises.
 */
@Injectable({ providedIn: 'root' })
export class SearchIndexService {
  private titleFreq = new Map<string, number>()
  private locationFreq = new Map<string, number>()
  private titlesOrdered: string[] = []
  private locationsOrdered: string[] = []
  private jobs: JobCatsOne[] = []
  private readonly indexed = new BehaviorSubject<void>(undefined)
  /** Emits after every initialize(), so open suggestion lists can recount (the home page indexes jobs late). */
  public readonly indexed$: Observable<void> = this.indexed.asObservable()
  private readonly synonyms: Record<string, string[]> = this.buildSynonyms()

  // How many items to surface when the user hasn't typed anything yet.
  private static readonly EMPTY_QUERY_LIMIT = 8

  public initialize(jobs: JobCatsOne[], staticTitles: string[], staticLocations: string[]): void {
    this.jobs = jobs
    this.titleFreq.clear()
    this.locationFreq.clear()

    // Case-insensitive dedupe, keep the best-cased representative we've seen.
    const titleCanonical = new Map<string, string>()
    const locationSet = new Set<string>()

    const addTitle = (raw: string): void => {
      for (const clean of this.extractProfessionHeads(raw)) {
        const key = this.normalize(clean)
        if (!key) continue
        if (!titleCanonical.has(key)) titleCanonical.set(key, clean)
        this.titleFreq.set(key, (this.titleFreq.get(key) || 0) + 1)
      }
    }

    for (const job of jobs) {
      addTitle(job.title || '')

      const city = (job.location?.city || '').trim()
      const state = (job.location?.state || '').trim()
      if (city && !this.isPostalCode(city)) {
        locationSet.add(city)
        this.locationFreq.set(city, (this.locationFreq.get(city) || 0) + 1)
      }
      if (state && !this.isPostalCode(state)) {
        locationSet.add(state)
        this.locationFreq.set(state, (this.locationFreq.get(state) || 0) + 1)
      }
    }

    for (const t of staticTitles) addTitle(t)

    for (const l of staticLocations) {
      if (l && !locationSet.has(l)) {
        locationSet.add(l)
        this.locationFreq.set(l, (this.locationFreq.get(l) || 0) + 1)
      }
    }

    this.titlesOrdered = Array.from(titleCanonical.values()).sort((a, b) => {
      const fa = this.titleFreq.get(this.normalize(a)) || 0
      const fb = this.titleFreq.get(this.normalize(b)) || 0
      if (fb !== fa) return fb - fa
      return a.localeCompare(b, 'nl', { sensitivity: 'base' })
    })

    this.locationsOrdered = Array.from(locationSet)
      .filter(loc => !this.isPostalCode(loc))
      .sort((a, b) => {
        const fa = this.locationFreq.get(a) || 0
        const fb = this.locationFreq.get(b) || 0
        if (fb !== fa) return fb - fa
        return a.localeCompare(b, 'nl', { sensitivity: 'base' })
      })

    this.indexed.next()
  }

  public suggestTitles(query: string, limit: number = 100): string[] {
    return this.suggest(query, this.titlesOrdered, (o) => this.titleFreq.get(this.normalize(o)) || 0, limit)
  }

  public suggestLocations(query: string, limit: number = 100): string[] {
    return this.suggest(query, this.locationsOrdered, (o) => this.locationFreq.get(o) || 0, limit)
  }

  /**
   * Profession suggestions that return at least one current vacancy, with that count. A suggestion
   * returning exactly the vacancies of one above it ("Netwerk" / "Network Engineer") is dropped.
   */
  public titleGroups(query: string, limit: number = 8): SearchSuggestionGroup[] {
    const seen = new Set<string>()
    const options: SearchSuggestion[] = []
    for (const value of this.suggestTitles(query, 40)) {
      const ids = this.jobs.filter(job => this.matchesTitle(job.title || '', value)).map(job => job.id)
      if (ids.length === 0) continue
      const key = ids.join(',')
      if (seen.has(key)) continue
      seen.add(key)
      options.push({ value, count: ids.length })
      if (options.length === limit) break
    }
    if (options.length === 0) return []
    return [{ label: this.normalize(query) ? 'Functies' : 'Populaire functies', options }]
  }

  /**
   * Provinces and places that have current vacancies, grouped, with the count the location filter
   * returns for each. Official names show as people say them ("Den Haag").
   */
  public placeGroups(query: string, limit: number = 8): SearchSuggestionGroup[] {
    const plain = (value: string) => value.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '')
    const places = new Map<string, { value: string, province: boolean }>()
    for (const job of this.jobs) {
      for (const [raw, province] of [[job.location?.state, true], [job.location?.city, false]] as const) {
        const value = displayCity(raw)
        if (!value || this.isPostalCode(value)) continue
        const key = plain(value)
        const known = places.get(key)
        if (known) known.province ||= province
        else places.set(key, { value, province })
      }
    }

    const wantedPlain = plain(query)
    const wantedPlace = normalizePlace(query)
    const ranked = Array.from(places.values())
      .filter(place => !wantedPlain || plain(place.value).includes(wantedPlain) || normalizePlace(place.value).includes(wantedPlace))
      .map(place => ({
        ...place,
        count: this.jobs.filter(job => matchesLocation(job, place.value)).length,
        prefix: wantedPlain !== '' && plain(place.value).startsWith(wantedPlain),
      }))
      .filter(place => place.count > 0)
      .sort((a, b) => Number(b.prefix) - Number(a.prefix) || b.count - a.count || a.value.localeCompare(b.value, 'nl'))

    const provinces = ranked.filter(place => place.province).slice(0, Math.ceil(limit / 2))
    const cities = ranked.filter(place => !place.province).slice(0, limit - provinces.length)
    const groups: SearchSuggestionGroup[] = []
    if (provinces.length) groups.push({ label: 'Provincies', options: provinces.map(({ value, count }) => ({ value, count })) })
    if (cities.length) groups.push({ label: 'Plaatsen', options: cities.map(({ value, count }) => ({ value, count })) })
    return groups
  }

  /**
   * Does `title` match `query` under the same rules the autocomplete uses?
   * Used by filterJobs so selecting a suggestion reliably returns results.
   */
  public matchesTitle(title: string, query: string): boolean {
    const q = this.normalize(query)
    if (!q) return true
    const t = this.normalize(title)
    if (!t) return false

    const candidates = this.queryCandidates(q)
    for (const cand of candidates) {
      if (!cand) continue
      if (t.includes(cand)) return true
      // Also accept when any vacancy-title token *starts with* the candidate,
      // so "sys" matches "systeembeheerder" (the autocomplete promises this).
      const tokens = this.tokenize(t)
      if (tokens.some(tok => tok.startsWith(cand))) return true
    }
    return false
  }

  private suggest(
    queryRaw: string,
    options: string[],
    freqOf: (option: string) => number,
    limit: number,
  ): string[] {
    const query = this.normalize(queryRaw)
    if (!query) {
      // On empty input, only show the top few by popularity — avoid flooding
      // the panel with every known profession/location.
      return options.slice(0, Math.min(limit, SearchIndexService.EMPTY_QUERY_LIMIT))
    }
    const MAX_EVALUATED = Math.min(1000, options.length)
    const FUZZY_BUDGET = 100
    let fuzzyRemaining = FUZZY_BUDGET

    const candidates = this.queryCandidates(query)

    type Scored = { option: string, score: number }
    const scored: Scored[] = []

    for (let i = 0; i < MAX_EVALUATED; i++) {
      const option = options[i]
      const norm = this.normalize(option)
      const frequency = freqOf(option)
      const freqScore = Math.log10(frequency + 1) * 100

      const optionTokens = this.tokenize(norm)
      const hasPrefixToken = optionTokens.some(t => t.startsWith(query))
      const hasContainsRaw = norm.includes(query)

      // Strict filter: require the raw query to show up as substring or token prefix.
      // Synonym-only matches are accepted via the candidates loop below.
      let score = 0

      if (norm === query) score += 1200
      if (hasPrefixToken) score += 1000
      if (hasContainsRaw) score += 500

      // Synonym-aware boosts (also promote synonym-only matches to be included).
      let synonymHit = false
      for (const cand of candidates) {
        if (!cand || cand === query) continue
        const synPrefix = optionTokens.some(t => t.startsWith(cand))
        const synContains = norm.includes(cand)
        if (synPrefix) { score += 300; synonymHit = true; break }
        if (synContains) { score += 150; synonymHit = true }
      }

      if (!hasContainsRaw && !hasPrefixToken && !synonymHit) {
        // Fuzzy fallback for near-misses on longer queries.
        if (query.length >= 4 && fuzzyRemaining > 0) {
          const likely = norm.charCodeAt(0) === query.charCodeAt(0)
          if (likely) {
            const d = this.editDistance(norm.slice(0, Math.min(norm.length, query.length + 2)), query)
            if (d <= 1) score += 400
            else if (d <= 2) score += 200
            fuzzyRemaining--
          }
        }
        if (score === 0) continue
      }

      score += freqScore
      if (score > 0) {
        scored.push({ option, score })
      }
      if (scored.length >= limit * 3 && fuzzyRemaining <= 0) {
        break
      }
    }

    scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score
      const fb = freqOf(b.option)
      const fa = freqOf(a.option)
      if (fb !== fa) return fb - fa
      return a.option.localeCompare(b.option, 'nl', { sensitivity: 'base' })
    })
    return scored.slice(0, limit).map(s => s.option)
  }

  /**
   * Split a raw job title into one or more profession "heads".
   *
   * CATS titles often embed specialism and location after an em-dash,
   * hyphen, pipe, or comma — e.g. "Infrastructuur Engineer – Linux – Utrecht".
   * We keep the leading role phrase and discard the tail.
   */
  private extractProfessionHeads(raw: string): string[] {
    const trimmed = (raw || '').trim()
    if (!trimmed) return []
    // Split on: em-dash, en-dash, hyphen with spaces, pipe, middle-dot, slash-with-spaces.
    // Hyphens inside words (e.g. "Front-End") are preserved by requiring spaces.
    const head = trimmed
      .split(/\s+[–—|·•]\s+|\s+-\s+|\s+\/\s+/u)[0]
      .replace(/\s{2,}/g, ' ')
      .trim()
    if (!head) return []
    return [head]
  }

  /**
   * Build the set of strings we'll compare a query against: the query itself
   * plus any synonyms (single-word OR multi-word keys).
   */
  private queryCandidates(normalizedQuery: string): string[] {
    const out = new Set<string>()
    out.add(normalizedQuery)

    // Whole-query synonym lookup handles multi-word keys like "software engineer".
    const wholeSyns = this.synonyms[normalizedQuery]
    if (wholeSyns) for (const s of wholeSyns) out.add(s)

    // Per-token synonym lookup for single-word queries & multi-word queries
    // where each word might have its own synonyms.
    const tokens = this.tokenize(normalizedQuery)
    for (const tok of tokens) {
      const syns = this.synonyms[tok]
      if (syns) for (const s of syns) out.add(s)
    }

    // Sliding bigrams for e.g. "cloud engineer" inside "senior cloud engineer".
    for (let i = 0; i < tokens.length - 1; i++) {
      const bigram = `${tokens[i]} ${tokens[i + 1]}`
      const syns = this.synonyms[bigram]
      if (syns) for (const s of syns) out.add(s)
    }

    return Array.from(out)
  }

  private normalize(value: string): string {
    return (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .trim()
  }

  private tokenize(value: string): string[] {
    return value.split(/[^a-z0-9]+/g).filter(Boolean)
  }

  private isPostalCode(value: string): boolean {
    const v = (value || '').trim().toUpperCase()
    return /^[0-9]{4}(\s?[A-Z]{2})?$/.test(v)
  }

  private editDistance(a: string, b: string): number {
    const m = a.length, n = b.length
    if (m === 0) return n
    if (n === 0) return m
    const dp = new Array(n + 1)
    for (let j = 0; j <= n; j++) dp[j] = j
    for (let i = 1; i <= m; i++) {
      let prev = i - 1
      dp[0] = i
      for (let j = 1; j <= n; j++) {
        const temp = dp[j]
        const cost = a[i - 1] === b[j - 1] ? 0 : 1
        dp[j] = Math.min(dp[j] + 1, dp[j - 1] + 1, prev + cost)
        prev = temp
      }
    }
    return dp[n]
  }

  private buildSynonyms(): Record<string, string[]> {
    const pairs: Array<[string, string]> = [
      ['ontwikkelaar', 'developer'],
      ['programmeur', 'developer'],
      ['software engineer', 'developer'],
      ['software ontwikkelaar', 'developer'],
      ['back-end', 'backend'],
      ['front-end', 'frontend'],
      ['systeembeheerder', 'system engineer'],
      ['systeembeheerder', 'sysadmin'],
      ['system administrator', 'sysadmin'],
      ['beheerder', 'administrator'],
      ['functiebeheerder', 'application manager'],
      ['functioneel beheer', 'application management'],
      ['applicatiebeheer', 'application management'],
      ['applicatiebeheerder', 'application manager'],
      ['tester', 'test engineer'],
      ['qa', 'test engineer'],
      ['scrummaster', 'scrum master'],
      ['product owner', 'po'],
      ['netwerkbeheerder', 'network engineer'],
      ['netwerk engineer', 'network engineer'],
      ['cloud engineer', 'devops'],
      ['security', 'information security'],
      ['data scientist', 'data engineer'],
      ['analist', 'analyst'],
      ['business analist', 'business analyst'],
      ['datawarehouse', 'data warehouse'],
    ]
    const map: Record<string, string[]> = {}
    const add = (a: string, b: string) => {
      const an = this.normalize(a)
      const bn = this.normalize(b)
      if (!an || !bn) return
      if (!map[an]) map[an] = []
      if (!map[bn]) map[bn] = []
      if (!map[an].includes(bn)) map[an].push(bn)
      if (!map[bn].includes(an)) map[bn].push(an)
    }
    for (const [a, b] of pairs) add(a, b)
    return map
  }
}
